/**
 * Loads Google reCAPTCHA only when a Forminator form is about to be used.
 *
 * functions/lazy-recaptcha.php replaces Forminator's api.js tag with an inert
 * `script[data-lazy-recaptcha]` placeholder. The real script is injected when a
 * captcha form comes within LOAD_MARGIN of the viewport, or is focused, tapped
 * or submitted, so pages whose forms stay below the fold never pay for it.
 */
class LazyRecaptcha {
  /**
   * @param {HTMLScriptElement} placeholder The inert tag holding the api.js URL.
   */
  constructor(placeholder) {
    this.src = placeholder.dataset.src;
    this.loaded = false;
    this.pending = new WeakSet();
    this.forms = [...document.querySelectorAll("form")].filter((form) =>
      form.querySelector(LazyRecaptcha.CAPTCHA_SELECTOR)
    );
    this.load = this.load.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  /**
   * Attach the triggers that load the library.
   *
   * @returns {void}
   */
  init() {
    if (!this.src || !this.forms.length) return;

    if ("IntersectionObserver" in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.load();
        },
        { rootMargin: LazyRecaptcha.LOAD_MARGIN }
      );
      this.forms.forEach((form) => this.observer.observe(form));
    } else {
      this.load();
    }

    this.forms.forEach((form) => {
      ["focusin", "pointerdown", "touchstart"].forEach((type) =>
        form.addEventListener(type, this.load, { once: true, passive: true })
      );
    });

    // Capture phase, so this runs before Forminator's own submit handler.
    document.addEventListener("submit", this.onSubmit, true);
  }

  /**
   * Inject api.js once.
   *
   * @returns {void}
   */
  load() {
    if (this.loaded) return;
    this.loaded = true;
    this.observer?.disconnect();

    const script = document.createElement("script");
    script.src = this.src;
    script.async = true;
    document.body.appendChild(script);
  }

  /**
   * Whether every captcha in a form has been rendered by Forminator.
   *
   * Forminator skips the captcha entirely when its container is still empty,
   * and the server then rejects the entry, so a submit must wait for this.
   *
   * @param {HTMLFormElement} form
   * @returns {boolean}
   */
  isReady(form) {
    return [...form.querySelectorAll(LazyRecaptcha.CAPTCHA_SELECTOR)].every(
      (el) => el.children.length > 0
    );
  }

  /**
   * Hold a submit made before the captcha exists, then replay it.
   *
   * @param {SubmitEvent} event
   * @returns {void}
   */
  onSubmit(event) {
    const form = event.target;
    if (!this.forms.includes(form) || form.dataset.lazyRecaptchaReplay || this.isReady(form)) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    // Repeat clicks while one submit is already held would each replay it.
    if (this.pending.has(form)) return;
    this.pending.add(form);
    this.load();

    const submitter = event.submitter;
    const started = Date.now();
    const replay = () => {
      if (!this.isReady(form) && Date.now() - started < LazyRecaptcha.READY_TIMEOUT) {
        setTimeout(replay, 100);
        return;
      }
      // After the timeout, let it through rather than leave the visitor stuck.
      this.pending.delete(form);
      form.dataset.lazyRecaptchaReplay = "1";
      this.resubmit(form, submitter);
      delete form.dataset.lazyRecaptchaReplay;
    };
    replay();
  }

  /**
   * Fire the form's submit handlers again.
   *
   * requestSubmit runs the normal submit event; without it (Safari < 16) the
   * jQuery trigger reaches Forminator's AJAX handler, where form.submit() would
   * bypass it and post the page.
   *
   * @param {HTMLFormElement} form
   * @param {HTMLElement|null} submitter
   * @returns {void}
   */
  resubmit(form, submitter) {
    if (typeof form.requestSubmit === "function") {
      form.requestSubmit(submitter);
    } else if (window.jQuery) {
      window.jQuery(form).trigger("submit");
    } else {
      form.submit();
    }
  }
}

LazyRecaptcha.CAPTCHA_SELECTOR = ".forminator-g-recaptcha";
LazyRecaptcha.LOAD_MARGIN = "400px 0px";
LazyRecaptcha.READY_TIMEOUT = 10000;

/**
 * Start the loader when the page carries a deferred reCAPTCHA.
 *
 * @returns {void}
 */
export default () => {
  const placeholder = document.querySelector("script[data-lazy-recaptcha]");
  if (placeholder) new LazyRecaptcha(placeholder).init();
};
